import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '@/contexts/auth-context'
import { useAuthActions } from '@convex-dev/auth/react'
import { useNavigate } from 'react-router-dom'
import { isElectron } from '@/lib/electron'

import { Navbar } from '@/components/homepage/sections/Navbar'
import { SectionHeader } from '@/components/homepage/ui/SectionHeader'
import { HomepageFooter } from '@/components/homepage/sections/HomepageFooter'
import { COPYRIGHT_EMAIL, LEGAL_EMAIL, OG_IMAGE_URL, siteUrl } from '@/lib/config/site'

export default function ContentPolicyPage() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { signIn } = useAuthActions()
  const handleSignUp = () => {
    if (isElectron()) {
      navigate('/signup')
      return
    }
    signIn('google', { redirectTo: `${window.location.origin}/auth` })
  }

  useEffect(() => { window.scrollTo(0, 0) }, [])

  return (
    <div className="min-h-screen bg-background px-4 lg:px-0 overflow-x-hidden">
      <Helmet>
        <title>Content Policy - Footage</title>
        <meta name="description" content="Content Policy for Footage.com. Learn what content is allowed and prohibited when using our AI video creation platform." />
        <link rel="canonical" href={siteUrl('/content-policy')} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Content Policy - Footage" />
        <meta property="og:description" content="Content Policy for Footage.com. Learn what content is allowed and prohibited when using our AI video creation platform." />
        <meta property="og:url" content={siteUrl('/content-policy')} />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:site_name" content="Footage" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Content Policy - Footage" />
        <meta name="twitter:description" content="Content Policy for Footage.com. Learn what content is allowed and prohibited when using our AI video creation platform." />
        <meta name="twitter:image" content={OG_IMAGE_URL} />
      </Helmet>

      <div className="max-w-7xl mx-auto border-x border-border">
        <Navbar isAuthenticated={isAuthenticated} onSignUp={handleSignUp} />
        <main className="flex flex-col divide-y divide-border pt-16">
          <SectionHeader>
            <div className="flex flex-col items-center justify-center gap-4 mt-4">
              <h1 className="text-3xl md:text-4xl lg:text-6xl font-medium tracking-tighter text-center text-balance">
                Content Policy
              </h1>
            </div>
          </SectionHeader>

          <section className="px-6 md:px-12 lg:px-20 py-12 md:py-16">
            <div className="max-w-3xl mx-auto prose prose-neutral dark:prose-invert">
              <p className="text-muted-foreground leading-relaxed">
                This Content Policy explains what content is allowed and prohibited when using Footage.com. This policy is part of, and should be read with, our{' '}
                <a href="/terms" className="text-foreground underline underline-offset-4 hover:text-primary">Terms of Service</a> and{' '}
                <a href="/privacy" className="text-foreground underline underline-offset-4 hover:text-primary">Privacy Policy</a>.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                By using Footage.com, you agree that your inputs (including text, images, video, audio, or other materials) and any outputs you generate or share through the service will comply with this Content Policy and all applicable laws.
              </p>

              <h2 className="text-xl md:text-2xl font-semibold mt-10 mb-4">1) Prohibited content</h2>
              <p className="text-muted-foreground leading-relaxed">
                You may not upload, generate, post, transmit, or otherwise make available through Footage.com any content that is:
              </p>
              <ul className="list-disc pl-6 mt-3 space-y-2 text-muted-foreground">
                <li><strong className="text-foreground">Pornographic, obscene, or indecent</strong>, including sexually explicit content.</li>
                <li><strong className="text-foreground">Threatening, harassing, hateful, degrading, or intimidating</strong>, or otherwise fails to respect the rights and dignity of others.</li>
                <li><strong className="text-foreground">Defamatory, libelous, fraudulent</strong>, or otherwise unlawful or tortious.</li>
                <li><strong className="text-foreground">Infringing or unauthorized</strong>, including content protected by copyright, trademark, trade secret, right of publicity or privacy, or any other proprietary right, unless you have the rights and permissions to use it.</li>
                <li><strong className="text-foreground">Malicious or harmful</strong>, including viruses, spyware, or other code intended to damage, hijack, or monitor systems.</li>
              </ul>

              <h2 className="text-xl md:text-2xl font-semibold mt-10 mb-4">2) Rights, permissions &amp; copyright infringement (DMCA)</h2>
              <p className="text-muted-foreground leading-relaxed">
                You are responsible for ensuring you have all rights, permissions, and authorizations needed to upload any materials to Footage.com and to use any outputs you generate. Do not upload or use content in a way that infringes or misappropriates the rights of any third party.
              </p>
              <p className="text-muted-foreground leading-relaxed mt-4">
                If you believe in good faith that materials available on or through Footage.com infringe your copyright, you may send a notice to our Copyright Manager as described in our{' '}
                <a href="/terms" className="text-foreground underline underline-offset-4 hover:text-primary">Terms of Service</a>. Notices should be sent to:
              </p>
              <address className="not-italic text-muted-foreground mt-4 leading-relaxed">
                <strong className="text-foreground">Copyright Manager</strong><br />
                Generative Platforms Inc.<br />
                11199 Sorrento Valley Rd Ste 209<br />
                San Diego, CA 92121<br />
                Phone: (888)-884-6175<br />
                Email: <a href={`mailto:${COPYRIGHT_EMAIL}`} className="text-foreground underline underline-offset-4 hover:text-primary">{COPYRIGHT_EMAIL}</a>
              </address>

              <h2 className="text-xl md:text-2xl font-semibold mt-10 mb-4">3) Responsible use</h2>
              <p className="text-muted-foreground leading-relaxed">
                You agree to use Footage.com responsibly and in good faith. Do not use the platform to generate misleading, deceptive, or harmful content, to impersonate others, or to produce content intended to harass, defraud, or cause harm to others.
              </p>

              <h2 className="text-xl md:text-2xl font-semibold mt-10 mb-4">4) Enforcement</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may remove content, restrict access, suspend features, or terminate accounts at any time if we believe content or behavior violates this Content Policy or our Terms of Service. You are responsible for maintaining backups of your content.
              </p>

              <h2 className="text-xl md:text-2xl font-semibold mt-10 mb-4">5) Updates</h2>
              <p className="text-muted-foreground leading-relaxed">
                We may update this Content Policy from time to time. Continued use of Footage.com after changes means you accept the updated policy.
              </p>

              <p className="text-muted-foreground leading-relaxed mt-10 pb-12">
                If you have questions about this Content Policy, please contact us at{' '}
                <a href={`mailto:${LEGAL_EMAIL}`} className="text-foreground underline underline-offset-4 hover:text-primary">{LEGAL_EMAIL}</a>.
              </p>
            </div>
          </section>

          <HomepageFooter />
        </main>
      </div>
    </div>
  )
}
